import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { credentialPdfBlob } from './credential-pdf';

interface DownloadSheetPdfButtonProps {
  a3WidthMm: number;
  a3HeightMm: number;
  renderDpi: number;
  filenameSlug: string;
}

async function captureA3PngDataUrl(): Promise<string> {
  const node = document.querySelector('[data-pdf-page]') as HTMLElement | null;
  if (!node) throw new Error('PDF page node not found');

  return toPng(node, {
    cacheBust: true,
    pixelRatio: 3,
    backgroundColor: '#ffffff',
  });
}

export function DownloadSheetPdfButton({
  a3WidthMm,
  a3HeightMm,
  renderDpi,
  filenameSlug,
}: DownloadSheetPdfButtonProps) {
  const [pending, setPending] = useState<null | 'server' | 'client'>(null);

  const handleServerDownload = async () => {
    setPending('server');
    try {
      const pngDataUrl = await captureA3PngDataUrl();
      const blob = await credentialPdfBlob(pngDataUrl);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `credenciales-${filenameSlug}-200dpi.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Server PDF failed', err);
      alert('Server PDF failed. Check console.');
    } finally {
      setPending(null);
    }
  };

  const handleClientDownload = async () => {
    setPending('client');
    try {
      const dataUrl = await captureA3PngDataUrl();
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a3',
        compress: true,
      });
      pdf.addImage(
        dataUrl,
        'PNG',
        0,
        0,
        a3WidthMm,
        a3HeightMm,
        undefined,
        'FAST',
      );
      pdf.save(`credenciales-${filenameSlug}-${renderDpi}dpi-client.pdf`);
    } catch (err) {
      console.error('Client PDF failed', err);
      alert('Client PDF failed. Check console.');
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button onClick={handleServerDownload} disabled={pending !== null}>
        {pending === 'server'
          ? 'Generating PDF…'
          : 'Download PDF (server, 200 dpi JPEG)'}
      </Button>
      <Button
        variant="outline"
        onClick={handleClientDownload}
        disabled={pending !== null}
      >
        {pending === 'client' ? 'Generating…' : 'Download PDF (client, jsPDF)'}
      </Button>
    </div>
  );
}
